import React, { useCallback, useState } from 'react'
import { Menu, MenuItem, ListItemIcon, ListItemText, makeStyles } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import { useUtilityPanel } from './UtilityPanelProvider'
import { TOOL_REGISTRY } from './tools/toolRegistry'

// Single entry point for opening any tool that lives in the utility
// panel. Replaces the per-tool launcher button. Style matches the
// `Manage` button in the views configBar so the toolbar reads as a
// family of menu triggers, not a pile of bespoke buttons.
const useStyles = makeStyles(() => ({
  trigger: {
    // Sizing intentionally matches the `viewsBtn` (Manage) trigger in
    // ExplorerConfigToolbar so the configBar reads as a row of peer
    // menu triggers. Keep these numbers in sync if Manage's styling
    // shifts.
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--color-neutral-700, #555)',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: 'var(--theme-color-surface-d2, #e8e8e8)'
    }
  },
  triggerActive: {
    // "A tool is currently open in the panel." Same surface tint as
    // the hover state so it feels like a depressed toggle button --
    // intentionally neutral, not AI-themed.
    background: 'var(--theme-color-surface-d2, #e8e8e8)',
    color: 'var(--color-neutral-900, #222)'
  },
  chevron: {
    fontSize: 10,
    opacity: 0.7
  },
  // "Something is running in the background." Small neutral dot, gentle
  // opacity pulse, no color shift. The panel's own border treatment is
  // still the primary cue when the panel is open; this is just a
  // reminder in the launcher row when it's closed.
  busyDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: 'var(--color-neutral-500, #888)',
    marginLeft: 4,
    flexShrink: 0,
    animation: '$dotPulse 1.6s ease-in-out infinite'
  },
  '@keyframes dotPulse': {
    '0%, 100%': { opacity: 0.35 },
    '50%': { opacity: 1 }
  },
  activeCheck: {
    minWidth: 28
  },
  // Tool icon swatch inside the menu items. The trigger itself stays
  // neutral, but each tool's icon is rendered in its own chip so users
  // can recognise tools at a glance once there's more than one entry.
  // `iconBackground: 'gradient'` (default) uses the AI gradient -- the
  // affordance research earned by being the first; `'none'` renders
  // the icon plain (for generic utility tools like allocation that
  // don't carry an AI association).
  toolIcon: {
    width: 22,
    height: 22,
    borderRadius: 4,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  toolIconGradient: {
    background: 'linear-gradient(135deg, var(--color-ai-500) 0%, var(--color-ai-600) 100%)',
    color: 'var(--color-contrast-ai-500)'
  },
  toolIconNone: {
    background: 'transparent',
    color: 'var(--color-neutral-700, #555)'
  }
}))

// Per-tool gating + busy state, driven by the registry. Each tool can
// optionally declare `useAvailable: () => boolean` and `useBusy: () =>
// boolean` Hooks in its registry entry; we call them here in stable
// registry iteration order.
//
// React's rules-of-hooks normally forbid calling hooks in a loop. This
// loop is safe because:
//   1. `TOOL_REGISTRY` is `Object.freeze`d and entries are only ever
//      APPENDED (never inserted mid-list).
//   2. `Object.values(literal)` iterates in declaration order, which
//      means hooks fire in the same order on every render.
//   3. Tools that omit either hook contribute zero hook calls.
//
// New tools MUST be appended to the registry, never inserted mid-list,
// or the hook order changes between renders and React will throw. See
// section 20 in DEVELOPMENT_NOTES.md.
function useToolStates () {
  const states = {}
  for (const tool of Object.values(TOOL_REGISTRY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const available = tool.useAvailable ? tool.useAvailable() : true
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const busy = tool.useBusy ? tool.useBusy() : false
    states[tool.id] = { available, busy }
  }
  return states
}

export default function UtilityMenuButton () {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const { isOpen, activeToolId, openTool, closeTool } = useUtilityPanel()
  const toolStates = useToolStates()

  const tools = Object.values(TOOL_REGISTRY)
  const availableTools = tools.filter((t) => toolStates[t.id]?.available !== false)
  const anyBusy = tools.some((t) => toolStates[t.id]?.busy)

  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), [])
  const handleClose = useCallback(() => setAnchorEl(null), [])

  const handlePick = useCallback((toolId) => {
    setAnchorEl(null)
    if (isOpen && activeToolId === toolId) {
      closeTool()
    } else {
      openTool(toolId)
    }
  }, [isOpen, activeToolId, openTool, closeTool])

  // No tools the user can actually reach -- hide the launcher entirely.
  // Keeps the toolbar tidy for users without research / future-tool
  // entitlements and means the configBar never shows a dead menu.
  if (availableTools.length === 0) return null

  const triggerClassName = [
    classes.trigger,
    isOpen ? classes.triggerActive : ''
  ].filter(Boolean).join(' ')

  return (
    <>
      <button
        type='button'
        className={triggerClassName}
        onClick={handleOpen}
        aria-haspopup='menu'
        aria-expanded={Boolean(anchorEl)}
      >
        <span>Utility</span>
        <span className={classes.chevron} aria-hidden='true'>▾</span>
        {anyBusy && <span className={classes.busyDot} aria-hidden='true' />}
      </button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {availableTools.map((tool) => {
          const isActive = isOpen && activeToolId === tool.id
          const busy = toolStates[tool.id]?.busy
          return (
            <MenuItem key={tool.id} onClick={() => handlePick(tool.id)}>
              <ListItemIcon className={classes.activeCheck}>
                {isActive
                  ? <CheckIcon fontSize='small' />
                  : tool.renderIcon
                    ? (
                      <span
                        className={`${classes.toolIcon} ${tool.iconBackground === 'none' ? classes.toolIconNone : classes.toolIconGradient}`}
                      >
                        {tool.renderIcon()}
                      </span>
                    )
                    : null}
              </ListItemIcon>
              <ListItemText
                primary={tool.label}
                secondary={busy ? 'In progress…' : null}
              />
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
